import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth/hooks/useAuth';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Download, LogOut, PlayCircle, Settings, User } from 'lucide-react';
import { FEATURES } from '@/config/appConfig';
import { useTutorial } from '@/features/onboarding/useTutorial';
import { usePwaInstall } from './pwa/usePwaInstall';
import { IosInstallInstructionsModal } from './pwa/IosInstallInstructionsModal';

export const UserInfo = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { startTutorial } = useTutorial();
  const { canInstall, isStandalone, promptInstall } = usePwaInstall();
  const [iosModalOpen, setIosModalOpen] = useState(false);

  if (!user) {
    return null;
  }

  const handleInstallClick = () => {
    if (canInstall) {
      promptInstall();
    } else {
      setIosModalOpen(true);
    }
  };

  // Use the best available name property, falling back to email if others don't exist.
  const displayName = user.name || user.nickname || user.email;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-gray-100 transition-colors">
          {/* Display name to the left of the picture — hidden on narrow screens so the
              header stays uncluttered; the avatar alone remains the tap target. */}
          <span className="hidden text-sm font-medium text-gray-800 sm:block sm:max-w-[120px] sm:truncate md:max-w-none">
            {displayName}
          </span>

          {/* Display user picture if available, otherwise a fallback avatar so the
              trigger is always visible/tappable (important now that the name can be hidden). */}
          {user.picture ? (
            <img
              src={user.picture}
              alt={displayName || 'User avatar'}
              className="h-8 w-8 md:h-10 md:w-10 shrink-0 rounded-full"
            />
          ) : (
            <div
              className="flex h-8 w-8 md:h-10 md:w-10 shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-500"
              aria-hidden="true"
            >
              <User className="h-5 w-5" />
            </div>
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64 max-w-[calc(100vw-2rem)] sm:w-70" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{user.name || 'User'}</p>
            <p className="text-xs leading-none text-gray-500">
              {user.email || 'No email provided'}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {FEATURES.USER_PROFILE && (
          <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
            <User className="mr-2 h-4 w-4" />
            <span>Profile</span>
          </DropdownMenuItem>
        )}
        {isAdmin && (
          <>
            <DropdownMenuItem
              onClick={() => navigate('/admin')}
              className="cursor-pointer"
            >
              <Settings className="mr-2 h-4 w-4" />
              <span>Admin panel</span>
            </DropdownMenuItem>
          </>
        )}
        {FEATURES.ONBOARDING_TUTORIAL && (
          <DropdownMenuItem
            onClick={() => { navigate('/'); startTutorial(); }}
            className="cursor-pointer"
          >
            <PlayCircle className="mr-2 h-4 w-4" />
            <span>Start Tutorial</span>
          </DropdownMenuItem>
        )}
        {!isStandalone && (
          <DropdownMenuItem onClick={handleInstallClick} className="cursor-pointer">
            <Download className="mr-2 h-4 w-4" />
            <span>Add to Home Screen</span>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => logout()} className="cursor-pointer">
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
      <IosInstallInstructionsModal open={iosModalOpen} onOpenChange={setIosModalOpen} />
    </DropdownMenu>
  );
};
